import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import { 
  Calendar, MapPin, Users, Clock, ArrowLeft, Plus, Minus, Trash2, 
  CreditCard, History, Package, AlertTriangle, CheckCircle2, 
  Loader2, DollarSign, Gift, ShoppingCart, ExternalLink, Briefcase,
  Check, CircleDollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingManagePage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // Workspace integration
  const [workspaceId, setWorkspaceId] = useState(null);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  
  // Dialogs
  const [showAddServiceDialog, setShowAddServiceDialog] = useState(false);
  const [showRemoveServiceDialog, setShowRemoveServiceDialog] = useState(false);
  const [showApplyCreditDialog, setShowApplyCreditDialog] = useState(false);
  const [serviceToRemove, setServiceToRemove] = useState(null);
  
  // Available services for adding
  const [availableServices, setAvailableServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  
  // New guest count
  const [newGuestCount, setNewGuestCount] = useState(0);
  
  // Credit to apply
  const [creditToApply, setCreditToApply] = useState(0);
  
  // Payment plan state
  const [payingId, setPayingId] = useState(null);
  
  // Payment status from URL
  const paymentStatus = searchParams.get('payment');

  const fetchBookingDetails = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookingData(response.data);
      
      // Initialize guest count
      const booking = response.data.booking;
      const guests = booking?.event_details?.guests || booking?.guest_count || 0;
      setNewGuestCount(guests);
    } catch (err) {
      console.error('Failed to fetch booking:', err);
      setError(err.response?.data?.detail || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  }, [bookingId, token]);

  useEffect(() => {
    fetchBookingDetails();
  }, [fetchBookingDetails]);

  // Check if workspace exists for this booking
  useEffect(() => {
    const checkWorkspace = async () => {
      if (!token || !bookingId) return;
      try {
        const response = await axios.get(`${API}/workspaces?booking_id=${bookingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const workspaces = response.data.workspaces || [];
        const existing = workspaces.find(w => w.booking_id === bookingId);
        if (existing) {
          setWorkspaceId(existing.workspace_id);
        }
      } catch (err) {
        // Workspace check failed, might not be admin
        console.log('Workspace check skipped');
      }
    };
    checkWorkspace();
  }, [token, bookingId]);

  const handleOpenWorkspace = async () => {
    if (workspaceId) {
      navigate(`/admin/workspaces/${workspaceId}`);
      return;
    }
    
    // Create workspace if it doesn't exist
    setCreatingWorkspace(true);
    try {
      const response = await axios.post(
        `${API}/workspaces?booking_id=${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/admin/workspaces/${response.data.workspace_id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const fetchAvailableServices = async () => {
    setServicesLoading(true);
    try {
      const response = await axios.get(`${API}/services/available-for-booking`);
      setAvailableServices(response.data.services || []);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    } finally {
      setServicesLoading(false);
    }
  };

  const handleUpdateGuests = async () => {
    const currentGuests = bookingData.booking?.event_details?.guests || bookingData.booking?.guest_count || 0;
    if (newGuestCount === currentGuests) return;
    
    setUpdating(true);
    try {
      const response = await axios.put(
        `${API}/bookings/${bookingId}/guests`,
        { new_guest_count: newGuestCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh booking data
      await fetchBookingDetails();
      
      if (response.data.store_credit_added > 0) {
        alert(`Guest count updated! $${response.data.store_credit_added.toFixed(2)} added to your store credit.`);
      } else if (response.data.requires_payment) {
        alert(`Guest count updated! Additional payment of $${response.data.price_change.toFixed(2)} required.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update guests');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddService = async (service) => {
    setUpdating(true);
    try {
      await axios.post(
        `${API}/bookings/${bookingId}/services`,
        {
          item_id: service.product_id,
          name: service.name,
          category: service.category,
          price: service.base_price,
          quantity: 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowAddServiceDialog(false);
      await fetchBookingDetails();
      alert('Service added successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add service');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveService = async () => {
    if (!serviceToRemove) return;
    
    setUpdating(true);
    try {
      const response = await axios.delete(
        `${API}/bookings/${bookingId}/services/${serviceToRemove.service_item_id || serviceToRemove.item_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowRemoveServiceDialog(false);
      setServiceToRemove(null);
      await fetchBookingDetails();
      alert(`Service removed! $${response.data.store_credit_added.toFixed(2)} added to your store credit.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove service');
    } finally {
      setUpdating(false);
    }
  };

  const handleApplyCredit = async () => {
    if (creditToApply <= 0) return;
    
    setUpdating(true);
    try {
      await axios.post(
        `${API}/bookings/${bookingId}/apply-credit`,
        { amount: creditToApply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowApplyCreditDialog(false);
      setCreditToApply(0);
      await fetchBookingDetails();
      alert('Store credit applied successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to apply credit');
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckout = async () => {
    setUpdating(true);
    try {
      const response = await axios.post(
        `${API}/bookings/${bookingId}/checkout-additions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        alert('No payment required!');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setUpdating(false);
    }
  };

  // Payment plan handlers
  const handlePayDeposit = async (planId) => {
    setPayingId('deposit');
    try {
      const res = await axios.post(`${API}/payment-plans/${planId}/pay-deposit`, {
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch {
      alert('Failed to start payment');
    } finally { setPayingId(null); }
  };

  const handlePayInstallment = async (planId, installmentId) => {
    setPayingId(installmentId);
    try {
      const res = await axios.post(`${API}/payment-plans/${planId}/pay-installment/${installmentId}`, {
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch {
      alert('Failed to start payment');
    } finally { setPayingId(null); }
  };

  const handlePayRemaining = async (planId) => {
    setPayingId('remaining');
    try {
      const res = await axios.post(`${API}/payment-plans/${planId}/pay-remaining`, {
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch {
      alert('Failed to start payment');
    } finally { setPayingId(null); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background pt-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/my-events')} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to My Events
          </Button>
        </div>
      </div>
    );
  }

  const booking = bookingData?.booking;
  const eventDate = booking?.event_details?.date || booking?.event_date;
  const guestCount = booking?.event_details?.guests || booking?.guest_count || 0;
  const location = booking?.event_details?.location || booking?.venue || 'TBD';
  const items = booking?.items || booking?.selected_items || [];
  
  const originalTotal = bookingData?.original_total || 0;
  const currentTotal = bookingData?.current_total || booking?.total || booking?.total_price || 0;
  const storeCredit = bookingData?.store_credit || 0;
  const creditApplied = booking?.store_credit_applied || 0;
  const reductionRemaining = bookingData?.reduction_remaining || 0;
  const canModify = bookingData?.can_modify;
  const modifyReason = bookingData?.modify_reason;
  
  const amountDue = Math.max(0, currentTotal - originalTotal - creditApplied);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/my-events')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to My Events
            </Button>
            {(user?.role === 'admin' || user?.role === 'staff') && (
              <Button 
                onClick={handleOpenWorkspace} 
                disabled={creatingWorkspace}
                data-testid="open-workspace-btn"
              >
                {creatingWorkspace ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Briefcase className="mr-2 h-4 w-4" />
                )}
                {workspaceId ? 'Open Workspace' : 'Create Workspace'}
              </Button>
            )}
          </div>
          <h1 className="text-3xl font-bold" data-testid="manage-booking-title">Manage Your Booking</h1>
          <p className="text-muted-foreground">Booking ID: {bookingId}</p>
        </div>

        {/* Payment Status Alert */}
        {paymentStatus === 'success' && (
          <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600">
              Payment successful! Your booking has been updated.
            </AlertDescription>
          </Alert>
        )}
        {paymentStatus === 'cancelled' && (
          <Alert className="mb-6 border-amber-500 bg-amber-50 dark:bg-amber-950">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-600">
              Payment was cancelled. Your booking changes are saved but not paid for yet.
            </AlertDescription>
          </Alert>
        )}

        {/* Modification Warning */}
        {!canModify && (
          <Alert className="mb-6" variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {modifyReason || 'Modifications are not allowed for this booking.'}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Details Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-500" />
                  Event Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{formatDate(eventDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium">{location}</p>
                    </div>
                  </div>
                </div>

                {/* Guest Count Editor */}
                <Separator />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Guest Count</p>
                      <p className="font-medium">{guestCount} guests</p>
                    </div>
                  </div>
                  {canModify && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setNewGuestCount(Math.max(1, newGuestCount - 1))}
                        disabled={updating || newGuestCount <= 1}
                        data-testid="decrease-guests"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="number"
                        value={newGuestCount}
                        onChange={(e) => setNewGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20 text-center"
                        data-testid="guest-count-input"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setNewGuestCount(newGuestCount + 1)}
                        disabled={updating}
                        data-testid="increase-guests"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      {newGuestCount !== guestCount && (
                        <Button
                          onClick={handleUpdateGuests}
                          disabled={updating}
                          data-testid="update-guests-btn"
                        >
                          {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Services Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-indigo-500" />
                    Services
                  </CardTitle>
                  <CardDescription>{items.length} service(s) in your booking</CardDescription>
                </div>
                {canModify && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      fetchAvailableServices();
                      setShowAddServiceDialog(true);
                    }}
                    data-testid="add-service-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Service
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div
                      key={item.service_item_id || item.item_id || index}
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                      data-testid={`service-item-${index}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.name}</p>
                          <Badge variant="outline" className="text-xs">
                            {item.category}
                          </Badge>
                          {item.added_via_modification && (
                            <Badge className="text-xs bg-green-500">Added</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Qty: {item.quantity} × ${item.price?.toFixed(2) || item.base_price?.toFixed(2) || '0.00'}
                        </p>
                        {item.selected_options?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Options: {item.selected_options.map(o => o.choice_name || o.name).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-semibold">
                          ${(item.total_price || item.price * item.quantity).toFixed(2)}
                        </p>
                        {canModify && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setServiceToRemove(item);
                              setShowRemoveServiceDialog(true);
                            }}
                            data-testid={`remove-service-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Modification History */}
            {bookingData?.modifications?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5 text-indigo-500" />
                    Modification History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {bookingData.modifications.map((mod, idx) => (
                      <div
                        key={mod.modification_id || idx}
                        className="flex items-start justify-between p-3 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{mod.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(mod.created_at).toLocaleString()}
                            {mod.is_admin && <Badge className="ml-2 text-xs">Admin</Badge>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${mod.price_change >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                            {mod.price_change >= 0 ? '+' : ''}${mod.price_change?.toFixed(2)}
                          </p>
                          {mod.store_credit_added > 0 && (
                            <p className="text-xs text-muted-foreground">
                              +${mod.store_credit_added.toFixed(2)} credit
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Summary */}
          <div className="space-y-6">
            {/* Store Credit Card */}
            <Card className="border-indigo-200 bg-indigo-50/50 dark:bg-indigo-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Gift className="h-5 w-5 text-indigo-500" />
                  Store Credit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-indigo-600">${storeCredit.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground mt-1">Available balance</p>
                {canModify && storeCredit > 0 && amountDue > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={() => setShowApplyCreditDialog(true)}
                    data-testid="apply-credit-btn"
                  >
                    Apply Credit
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Payment Plan Schedule */}
            {bookingData?.payment_plan && (
              <Card className="border-indigo-200" data-testid="booking-payment-plan">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CircleDollarSign className="h-5 w-5 text-indigo-500" />
                    Payment Plan
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {bookingData.payment_plan.auto_charge ? 'Auto-pay enabled' : 'Manual payments'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Progress */}
                  {(() => {
                    const pp = bookingData.payment_plan;
                    const progressPct = pp.total > 0 ? Math.min(100, (pp.total_paid / pp.total) * 100) : 0;
                    const allPaid = pp.plan_status === 'completed' || pp.remaining <= 0.01;
                    return (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Paid</span>
                          <span className="font-semibold text-green-600">${pp.total_paid.toFixed(2)}</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{progressPct.toFixed(0)}%</span>
                          <span>${pp.remaining.toFixed(2)} remaining</span>
                        </div>

                        {/* Deposit Row */}
                        <div className={`flex items-center justify-between p-3 rounded-lg border ${pp.deposit_status === 'paid' ? 'border-green-500/30 bg-green-50 dark:bg-green-950/20' : 'border-amber-500/30 bg-amber-50 dark:bg-amber-950/20'}`}>
                          <div className="flex items-center gap-2">
                            {pp.deposit_status === 'paid' 
                              ? <Check className="h-4 w-4 text-green-500" />
                              : <DollarSign className="h-4 w-4 text-amber-500" />
                            }
                            <div>
                              <p className="text-sm font-medium">Deposit</p>
                              <p className="text-xs text-muted-foreground">${pp.deposit_amount.toFixed(2)}</p>
                            </div>
                          </div>
                          {pp.deposit_status === 'paid' 
                            ? <Badge className="bg-green-500 text-white border-0 text-xs">Paid</Badge>
                            : <Button size="sm" variant="outline" onClick={() => handlePayDeposit(pp.plan_id)} disabled={payingId === 'deposit'} data-testid="manage-pay-deposit">
                                {payingId === 'deposit' ? '...' : 'Pay'}
                              </Button>
                          }
                        </div>

                        {/* Installments */}
                        {pp.installments.map((inst, idx) => {
                          const isPaid = inst.status === 'paid';
                          const isOverdue = !isPaid && new Date(inst.due_date) < new Date();
                          return (
                            <div key={inst.installment_id} className={`flex items-center justify-between p-3 rounded-lg border ${isPaid ? 'border-green-500/30 bg-green-50 dark:bg-green-950/20' : isOverdue ? 'border-red-500/30 bg-red-50 dark:bg-red-950/20' : 'border-border'}`}>
                              <div className="flex items-center gap-2">
                                {isPaid 
                                  ? <Check className="h-4 w-4 text-green-500" />
                                  : <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{idx + 1}</span>
                                }
                                <div>
                                  <p className="text-sm font-medium">Payment {idx + 1}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {isPaid ? 'Paid' : `Due ${new Date(inst.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                                    {isOverdue && !isPaid && <span className="text-red-500 ml-1">Overdue</span>}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold">${inst.amount.toFixed(2)}</span>
                                {isPaid 
                                  ? <Badge className="bg-green-500 text-white border-0 text-xs">Paid</Badge>
                                  : pp.deposit_status === 'paid' 
                                    ? <Button size="sm" variant={isOverdue ? 'destructive' : 'outline'} onClick={() => handlePayInstallment(pp.plan_id, inst.installment_id)} disabled={payingId === inst.installment_id} className="text-xs h-7" data-testid={`manage-pay-inst-${idx}`}>
                                        {payingId === inst.installment_id ? '...' : 'Pay'}
                                      </Button>
                                    : <Badge variant="secondary" className="text-xs">Pending</Badge>
                                }
                              </div>
                            </div>
                          );
                        })}

                        {/* Pay Remaining / View Invoice */}
                        {!allPaid && pp.deposit_status === 'paid' && pp.remaining > 0.01 && (
                          <Button 
                            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-sm" 
                            size="sm"
                            onClick={() => handlePayRemaining(pp.plan_id)}
                            disabled={payingId === 'remaining'}
                            data-testid="manage-pay-remaining"
                          >
                            {payingId === 'remaining' ? 'Processing...' : `Pay All — $${pp.remaining.toFixed(2)}`}
                          </Button>
                        )}
                        {allPaid && (
                          <div className="text-center py-2 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-500/30">
                            <p className="text-sm font-semibold text-green-600"><Check className="h-4 w-4 inline mr-1" />Paid in Full</p>
                          </div>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full text-xs"
                          onClick={() => navigate(`/invoice/${bookingData.payment_plan.quote_id}`)}
                          data-testid="view-live-invoice-btn"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" /> View Live Invoice
                        </Button>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Price Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="h-5 w-5 text-indigo-500" />
                  Price Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Original Total</span>
                  <span>${originalTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Total</span>
                  <span>${currentTotal.toFixed(2)}</span>
                </div>
                {creditApplied > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Credit Applied</span>
                    <span>-${creditApplied.toFixed(2)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Amount Due</span>
                  <span className={amountDue > 0 ? 'text-amber-600' : 'text-green-600'}>
                    ${amountDue.toFixed(2)}
                  </span>
                </div>
                
                {canModify && (
                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground mb-1">
                      Reduction remaining: ${reductionRemaining.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      (Max 20% of original can be reduced)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Button */}
            {amountDue > 0 && (
              <Button
                className="w-full"
                size="lg"
                onClick={handleCheckout}
                disabled={updating}
                data-testid="pay-additions-btn"
              >
                {updating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                Pay ${amountDue.toFixed(2)}
              </Button>
            )}

            {/* Modification Rules */}
            <Card className="bg-muted/30">
              <CardContent className="pt-4">
                <h4 className="font-medium mb-2">Modification Rules</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Changes allowed up to 72 hours before event</li>
                  <li>• Reductions up to 20% become store credit</li>
                  <li>• Additional services require payment</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Service Dialog */}
      <Dialog open={showAddServiceDialog} onOpenChange={setShowAddServiceDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Service to Booking</DialogTitle>
            <DialogDescription>
              Select a service to add to your event
            </DialogDescription>
          </DialogHeader>
          
          {servicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {availableServices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No additional services available
                </p>
              ) : (
                availableServices.map(service => (
                  <div
                    key={service.product_id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                  >
                    <div>
                      <p className="font-medium">{service.name}</p>
                      <p className="text-sm text-muted-foreground">{service.category}</p>
                      <p className="text-sm">${service.base_price?.toFixed(2)}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddService(service)}
                      disabled={updating}
                    >
                      <Plus className="mr-1 h-4 w-4" /> Add
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Service Confirmation Dialog */}
      <Dialog open={showRemoveServiceDialog} onOpenChange={setShowRemoveServiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Service</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "{serviceToRemove?.name}"?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert>
              <Gift className="h-4 w-4" />
              <AlertDescription>
                The refund amount will be added to your store credit balance.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveServiceDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveService}
              disabled={updating}
            >
              {updating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Remove Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Credit Dialog */}
      <Dialog open={showApplyCreditDialog} onOpenChange={setShowApplyCreditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Store Credit</DialogTitle>
            <DialogDescription>
              Available credit: ${storeCredit.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Amount to apply</label>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-muted-foreground">$</span>
              <Input
                type="number"
                value={creditToApply}
                onChange={(e) => setCreditToApply(Math.min(Math.min(storeCredit, amountDue), parseFloat(e.target.value) || 0))}
                max={Math.min(storeCredit, amountDue)}
                step="0.01"
                data-testid="credit-amount-input"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Max applicable: ${Math.min(storeCredit, amountDue).toFixed(2)}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyCreditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApplyCredit}
              disabled={updating || creditToApply <= 0}
            >
              {updating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Apply ${creditToApply.toFixed(2)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
